import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupLabel,
} from "@babackapp/customer-ui/components/input-group";
import { Separator } from "@babackapp/customer-ui/components/separator";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import {
  Alert,
  AlertDescription,
} from "@babackapp/customer-ui/components/alert";
import { EnterIcon } from "@radix-ui/react-icons";
import { AlertCircleIcon, ArrowLeft } from "lucide-react";
import { motion } from "motion/react";
import { Controller, useWatch } from "react-hook-form";
import { Link } from "react-router-dom";
import { useAddressForm } from "../hooks/use-address-form";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { CountryCombobox } from "@babackapp/return-widget-components/components/country-combobox";
import { ProvinceSelect } from "@babackapp/return-widget-components/components/province-select";
import { provincesOf } from "@babackapp/return-widget-components/utils/provinces";
import { UserPortalCallout } from "@babackapp/return-widget-components/components/user-portal-callout";
import { useFocusFirstError } from "@babackapp/return-widget-components/hooks/use-focus-first-error";

// Text fields only: the country and state pickers register no ref to focus.
const ADDRESS_FIELDS_IN_ORDER = [
  "companyName",
  "first_name",
  "last_name",
  "address1",
  "address2",
  "city",
  "zip",
  "email",
  "phone",
] as const;
const COUNTRY_LABEL_ID = "address-check-country-label";
const PROVINCE_SELECT_ID = "address-check-province";

export function ReturnWidgetAddressCheck() {
  const { form, onSubmit, userPortalTexts } = useAddressForm();
  const t = useCustomText();
  const countryCode = useWatch({ control: form.control, name: "country_code" });
  const provinces = provincesOf(countryCode);
  useFocusFirstError(form, ADDRESS_FIELDS_IN_ORDER);

  const errors = form.formState.isSubmitted
    ? Object.entries(form.formState.errors).flatMap(([name, e]) =>
        e?.message ? [{ name, message: t(e.message) }] : [],
      )
    : [];
  // Each field in error points at its own line of the summary, read when
  // useFocusFirstError focuses it (BBS-438).
  const errorId = (name: string) => `address-check-${name}-error`;
  const errorProps = (name: string) => {
    const invalid = errors.some((e) => e.name === name);
    return {
      "aria-invalid": invalid || undefined,
      "aria-describedby": invalid ? errorId(name) : undefined,
    };
  };

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{t("addressCheck.title")}</HeaderTitle>
        <HeaderDescription>{t("addressCheck.description")}</HeaderDescription>
      </Header>
      <UserPortalCallout texts={userPortalTexts} />

      {errors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertDescription>
            <ul className="list-disc pl-4">
              {errors.map(({ name, message }) => (
                <li key={name} id={errorId(name)}>
                  {message}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={onSubmit}>
        <motion.div
          initial={{ opacity: 0, x: 15 }}
          animate={{ opacity: 1, x: 0, transition: { delay: 0.1 } }}
          exit={{ opacity: 0, x: -15 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-6 gap-2"
        >
          <Controller
            control={form.control}
            name="companyName"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-6">
                <InputGroupInput
                  placeholder={t("common.fields.companyName")}
                  {...errorProps("companyName")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>
                    {t("common.fields.companyName")}
                  </InputGroupLabel>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="first_name"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.firstName")}
                  {...errorProps("first_name")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>
                    {t("common.fields.firstName")}
                  </InputGroupLabel>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="last_name"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-4 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.lastNameShort")}
                  {...errorProps("last_name")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>
                    {t("common.fields.lastName")}
                  </InputGroupLabel>
                </InputGroupAddon>
              </InputGroup>
            )}
          />

          <Separator className="col-span-6 my-4" />

          <Controller
            control={form.control}
            name="address1"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.address1")}
                  {...errorProps("address1")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>
                    {t("common.fields.address1")}
                  </InputGroupLabel>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="address2"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-4 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.address2Placeholder")}
                  {...errorProps("address2")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>
                    {t("common.fields.address2")}
                  </InputGroupLabel>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="city"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-2">
                <InputGroupInput
                  placeholder={t("common.fields.city")}
                  {...errorProps("city")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>{t("common.fields.city")}</InputGroupLabel>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="zip"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-3 col-span-2">
                <InputGroupInput
                  placeholder={t("common.fields.zip")}
                  {...errorProps("zip")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>{t("common.fields.zip")}</InputGroupLabel>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          {/*
            A picker, not a text input: this field feeds `shipping_address.country_code`,
            which carrier compatibility matches by strict equality against ISO 3166-1
            alpha-2 lists. A hand-typed "France" resolved no carrier and left the return
            without a label — or blocked it at approval on shops without
            `canReturnWithoutLabel`.
          */}
          <Controller
            control={form.control}
            name="country_code"
            render={({ field }) => (
              <div className="col-start-5 col-span-2 flex flex-col gap-1">
                {/* Matches the package's own country cell in gift-address.page.tsx,
                    not the InputGroupAddon labels of the sibling text fields. */}
                <span
                  id={COUNTRY_LABEL_ID}
                  className="text-xs text-muted-foreground px-0.5"
                >
                  {t("common.fields.country")}
                </span>
                <CountryCombobox
                  labelledBy={COUNTRY_LABEL_ID}
                  {...errorProps("country_code")}
                  value={field.value}
                  onChange={(iso) => {
                    field.onChange(iso);
                    // A state belongs to one country: keep it only if the new
                    // country still lists it.
                    const current = form.getValues("province_code");
                    if (!provincesOf(iso)?.some((p) => p.code === current)) {
                      form.setValue("province_code", "");
                    }
                  }}
                  placeholder={t("common.fields.country")}
                />
              </div>
            )}
          />
          {provinces && (
            <Controller
              control={form.control}
              name="province_code"
              render={({ field, fieldState }) => (
                <div className="col-start-1 col-span-3 flex flex-col gap-1">
                  <label
                    htmlFor={PROVINCE_SELECT_ID}
                    className="text-xs text-muted-foreground px-0.5"
                  >
                    {t("common.fields.province")}
                  </label>
                  <ProvinceSelect
                    id={PROVINCE_SELECT_ID}
                    aria-describedby={
                      errorProps("province_code")["aria-describedby"]
                    }
                    provinces={provinces}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("common.fields.province")}
                    isInvalid={!!fieldState.error}
                  />
                </div>
              )}
            />
          )}

          <Separator className="col-span-6 my-4" />

          <Controller
            control={form.control}
            name="email"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.email")}
                  type="email"
                  {...errorProps("email")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>{t("common.fields.email")}</InputGroupLabel>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="phone"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-4 col-span-3">
                <InputGroupInput
                  placeholder="+33 6 00 00 00 00"
                  type="tel"
                  {...errorProps("phone")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>{t("common.fields.phone")}</InputGroupLabel>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
        </motion.div>

        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer
            layoutId="line-item-back-button"
            disableVariants={{ exit: true }}
          >
            <Link to="../return-mode">
              <Button variant="secondary" type="button">
                <ArrowLeft />
                {t("common.back")}
              </Button>
            </Link>
          </AnimatedButtonContainer>
          <AnimatedButtonContainer
            layoutId="line-item-confirm-button"
            variants={{}}
          >
            <Button type="submit">
              <EnterIcon />
              {t("common.confirm")}
            </Button>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      </form>
    </WidgetPage>
  );
}
